// src/components/Navbar.jsx
import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import { UserRole } from '../apis/api-client';
import {
  AccountCircleIcon,
  BarChartIcon,
  BookmarkIcon,
  CertificateIcon,
  ChevronRightIcon,
  GroupIcon,
  LabelIcon,
  LibraryBooksIcon,
  PersonIcon,
  SchoolIcon,
  SettingsIcon,
  WorkspacesIcon,
} from './icons';
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  NavigationDrawer,
  NavigationListItem,
} from './ui';
import SidebarLogo from './nav/SidebarLogo';
import SidebarUserMenu from './nav/SidebarUserMenu';
import { useOwnedGroups } from '../hooks/useOwnedGroups';
import { useSelector } from 'react-redux';
import { RootState } from '../types';

interface NavItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  roles: UserRole[] | null;
  /** When set, the item renders as a collapsible parent and the
   *  children sit indented underneath. The parent's `path` is still
   *  navigable; the chevron toggles expansion. */
  children?: NavItem[];
}

export default function Navbar() {
  const theme = useTheme();
  const location = useLocation();
  const userRole = useSelector((state: RootState) => state.profile.user?.role);
  // A learner who owns a group sees ONLY the Groups entry (see visibleFor).
  const { isGroupAdmin } = useOwnedGroups();
  const isLearnerGroupAdmin = userRole === UserRole.User && isGroupAdmin;

  // Define all navigation items. Settings is now a parent group with
  // Tags and Content custom fields nested under it — both are
  // admin-only "content-shaping" surfaces that read naturally as
  // sub-settings rather than top-level destinations.
  const allNavItems: NavItem[] = [
    { text: 'Content', icon: <SchoolIcon />, path: '/courses', roles: null },
    { text: 'Workspaces', icon: <WorkspacesIcon />, path: '/workspaces', roles: [UserRole.Admin, UserRole.AccountAdmin] },
    { text: 'Users', icon: <PersonIcon />, path: '/students', roles: [UserRole.Admin, UserRole.AccountAdmin, UserRole.WorkspaceAdmin] },
    // Groups is a flat, security-scoped surface (the page narrows to the
    // user's workspace for a Workspace Admin, their account for an Account
    // Admin). It mirrors Users/Assignments as a top-level destination so a
    // Workspace Admin — who can't reach the Account-Admin-only Workspaces
    // page — still has a path to manage their workspace's groups. Learner
    // group admins also get this entry (only this one — see visibleFor).
    { text: 'Groups', icon: <GroupIcon />, path: '/groups', roles: [UserRole.Admin, UserRole.AccountAdmin, UserRole.WorkspaceAdmin] },
    { text: 'Assignments', icon: <BookmarkIcon />, path: '/assignments', roles: [UserRole.Admin, UserRole.AccountAdmin, UserRole.WorkspaceAdmin] },
    { text: 'Reports', icon: <BarChartIcon />, path: '/reports', roles: [UserRole.Admin, UserRole.AccountAdmin, UserRole.WorkspaceAdmin] },
    {
      text: 'Settings',
      icon: <SettingsIcon />,
      path: '/settings',
      roles: [UserRole.Admin, UserRole.AccountAdmin],
      children: [
        {
          // Certificate template is an account-wide content-shaping surface —
          // Account Admins manage it for their account alongside Instructors +
          // Tags. (Backend must scope the template + its save endpoint to the
          // caller's account, not LMS-wide.)
          text: 'Certificate template',
          icon: <CertificateIcon />,
          path: '/settings',
          roles: [UserRole.Admin, UserRole.AccountAdmin],
        },
        {
          // Course instructors are account-shared (CourseAuthor.accountId,
          // no per-workspace scoping; /api/CourseAuthors is account-wide), so
          // they stay with Account/Super Admin — a Workspace Admin editing a
          // shared instructor would bleed into other workspaces.
          text: 'Course instructors',
          icon: <AccountCircleIcon />,
          path: '/settings/instructors',
          roles: [UserRole.Admin, UserRole.AccountAdmin],
        },
        { text: 'Tags', icon: <LabelIcon />, path: '/tags', roles: [UserRole.Admin, UserRole.AccountAdmin] },
        {
          // Content custom fields are account-wide content-shaping surfaces,
          // same class as the certificate template above — Account Admins
          // manage them for their own account. (Backend must scope reads +
          // writes to the caller's account.)
          text: 'Content custom fields',
          icon: <LibraryBooksIcon />,
          path: '/settings/custom-fields',
          roles: [UserRole.Admin, UserRole.AccountAdmin],
        },
      ],
    },
  ];

  // Filter nav items + their children by the effective role. A learner
  // group admin is a special case: they get the Groups entry and nothing
  // else (not even the learner-visible Content item), keeping their shell
  // focused on the one admin surface they have.
  const visibleFor = (item: NavItem): boolean =>
    isLearnerGroupAdmin
      ? item.path === '/groups'
      : item.roles == null || item.roles.some((r) => r === userRole);
  const navItems = allNavItems
    .filter(visibleFor)
    .map((item) =>
      item.children
        ? { ...item, children: item.children.filter(visibleFor) }
        : item,
    );

  // Per-parent expanded state. Auto-opens on mount and whenever the
  // current route belongs to one of a parent's children, so navigating
  // to /tags via a deep link reveals the Settings group automatically.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  // Stable signature of the collapsible groups' child paths. `navItems` is a
  // fresh array each render (so it can't be a dep), but this string only
  // changes when the visible groups/children do.
  const navGroupSignature = navItems
    .filter((i) => i.children)
    .map((i) => `${i.text}:${i.children!.map((c) => c.path).join(',')}`)
    .join('|');

  useEffect(() => {
    setOpenGroups((cur) => {
      const next = { ...cur };
      let changed = false;
      for (const item of navItems) {
        if (!item.children) continue;
        const childMatches = item.children.some((c) =>
          location.pathname.startsWith(c.path),
        );
        if (childMatches && !cur[item.text]) {
          next[item.text] = true;
          changed = true;
        }
      }
      return changed ? next : cur;
    });
    // Depend on the group signature too, not just pathname: when navItems fills
    // in after mount (e.g. the role resolves on a deep-link), the matching
    // group still auto-expands even though the path never changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, navGroupSignature]);

  const toggleGroup = (key: string) =>
    setOpenGroups((cur) => ({ ...cur, [key]: !cur[key] }));

  const isPathActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  // Among a set of sibling paths (a Settings group's children), return
  // the one that best matches the current URL — the LONGEST path that's
  // either equal to or a prefix of the URL. Used so that visiting
  // `/settings/instructors` only highlights "Course instructors" and not
  // "Certificate template" (which lives at `/settings` and would
  // otherwise win on a naive prefix check).
  const longestActivePath = (paths: string[]): string | null => {
    let best: string | null = null;
    for (const p of paths) {
      if (!isPathActive(p)) continue;
      if (best == null || p.length > best.length) best = p;
    }
    return best;
  };

  return (
    <NavigationDrawer>
      <SidebarLogo />

      <List sx={{ width: '100%' }}>
        {navItems.map((item) => {
          if (!item.children || item.children.length === 0) {
            return (
              <ListItem key={item.text} disablePadding>
                <NavigationListItem
                  component={Link}
                  to={item.path}
                  // Precise match (exact or `path/` prefix), same as the
                  // parent rows below. A loose `includes` lit up multiple
                  // items on nested routes — e.g. /workspaces/:id/groups/:id
                  // highlighted BOTH Workspaces and the top-level Groups.
                  selected={isPathActive(item.path)}
                >
                  <ListItemIcon sx={{ color: theme.palette.primary.main }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </NavigationListItem>
              </ListItem>
            );
          }

          // Parent row + collapsible children.
          const open = !!openGroups[item.text];
          const parentSelected = isPathActive(item.path);
          // Resolve which child (if any) is the best match for the
          // current URL. Used below so only the most-specific sibling
          // highlights — e.g. at /settings/instructors only "Course
          // instructors" lights up, not "Certificate template" (which
          // sits at /settings and would otherwise match by prefix too).
          const childPaths = item.children.map((c) => c.path);
          const activeChildPath = longestActivePath(childPaths);
          const anyChildSelected = activeChildPath != null;

          return (
            <Box key={item.text}>
              <ListItem disablePadding>
                {/* Parent row is a TOGGLE, not a navigation target —
                    "Settings" itself isn't a page, the actual pages
                    live one level down (Certificate template,
                    Instructors, …). Without this, clicking Settings
                    routed straight to the certificate editor and made
                    the dropdown feel like a side-effect. */}
                <NavigationListItem
                  selected={anyChildSelected || parentSelected}
                  onClick={() => toggleGroup(item.text)}
                >
                  <ListItemIcon sx={{ color: theme.palette.primary.main }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                  <Box
                    component="span"
                    aria-label={open ? 'Collapse' : 'Expand'}
                    onClick={(e) => {
                      // Chevron still has its own stopPropagation so a
                      // keyboard / a11y user clicking the chevron
                      // explicitly doesn't double-toggle.
                      e.preventDefault();
                      e.stopPropagation();
                      toggleGroup(item.text);
                    }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      ml: 1,
                      p: 0.25,
                      borderRadius: 1,
                      color: 'text.secondary',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <ChevronRightIcon
                      fontSize="small"
                      sx={{
                        transform: open ? 'rotate(90deg)' : 'rotate(0)',
                        transition: 'transform 150ms ease',
                      }}
                    />
                  </Box>
                </NavigationListItem>
              </ListItem>

              <Collapse in={open} timeout="auto" unmountOnExit>
                <List disablePadding>
                  {item.children.map((child) => (
                    <ListItem key={child.text} disablePadding>
                      <NavigationListItem
                        component={Link}
                        to={child.path}
                        selected={child.path === activeChildPath}
                        // Indent so children read as nested without
                        // needing a dedicated style variant.
                        sx={{ pl: 5 }}
                      >
                        <ListItemIcon
                          sx={{
                            color: theme.palette.primary.main,
                            minWidth: 32,
                          }}
                        >
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={child.text}
                          primaryTypographyProps={{ fontSize: '0.9rem' }}
                        />
                      </NavigationListItem>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>

      {/* User identity + logout, pinned to the bottom of the sidebar. */}
      <SidebarUserMenu />
    </NavigationDrawer>
  );
}
